import { cn } from "@/lib/utils.ts";
import type { ArtifactComponent } from "@/types/artifacts-view.ts";
import { Bot, User } from "lucide-react";

type TranscriptComponentData = Extract<ArtifactComponent, { type: "transcript" }>;
type Message = TranscriptComponentData["messages"][number];

const formatTimestamp = (secondsFromStart: number): string => {
  const minutes = Math.floor(secondsFromStart / 60);
  const seconds = secondsFromStart % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

const speakerIcon = (role: Message["role"]) => {
  if (role === "user") return User;
  return Bot;
};

const defaultSpeakerLabel = (role: Message["role"]) => {
  if (role === "user") return "Caller";
  if (role === "agent") return "Assistant";
  return "Other";
};

const MessageBubble = ({ message }: { message: Message }) => {
  const isUser = message.role === "user";
  const Icon = speakerIcon(message.role);
  return (
    <div className={cn("flex gap-3 p-3 rounded-lg", isUser ? "bg-primary/10" : "bg-gray-50")}>
      <div className="flex-shrink-0">
        <div
          className={cn(
            "w-8 h-8 rounded-full flex items-center justify-center",
            isUser ? "bg-primary/20" : "bg-gray-300",
          )}
        >
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-medium text-gray-600">
            {message.speakerLabel ?? defaultSpeakerLabel(message.role)}
          </span>
          {message.timestampSeconds !== undefined ? (
            <span className="text-xs text-gray-400">
              {formatTimestamp(message.timestampSeconds)}
            </span>
          ) : null}
        </div>
        <div className="text-sm text-gray-800 break-words">{message.text}</div>
      </div>
    </div>
  );
};

export const TranscriptBlock = ({ component }: { component: TranscriptComponentData }) => {
  return (
    <div className="flex flex-col gap-2 bg-white p-3 rounded">
      {component.messages.map((message, index) => (
        <MessageBubble key={index} message={message} />
      ))}
    </div>
  );
};
